import React, { useState, useEffect, useCallback, useRef } from "react";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MPHome from "./MPHome";
import MPHomeApps from "./BarberMainPageComps/MPHomeApps";
import moment from "moment";
import axios from "axios";
import { MyDomain } from "../App";
import { useAppContext } from "../AppContext";
import { useParams } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { useTranslation } from "react-i18next";

// ─── Design tokens (same as AllCategories) ────────────────────────────────────
const C = {
  bg: "#F4F5F7",
  surface: "#FFFFFF",
  primary: "#1A1A2E",
  accent: "#C9A84C",
  accentLight: "#FDF6E3",
  text: "#1A1A2E",
  textSub: "#64748B",
  textMuted: "#A0AEC0",
  border: "#E8ECF0",
};

const LANGUAGES = [
  { code: "sv", name: "Svenska", flag: "🇸🇪" },
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
];

export default function BarberMainPage() {
  const { t, i18n } = useTranslation();
  const { BarberPhoneNumber } = useParams();
  const hubRef = useRef(null);
  const userRef = useRef(null);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [hoveredLang, setHoveredLang] = useState(null);

  const {
    OpenDrawer,
    setOpenDrawer,
    setAllApps,
    Loading,
    setLoading,
    setChoosedDate,
    setChoosedApps,
    setAllData,
    setUser,
    setAppContextLoading,
    setOtherBarbers,
    setValue,
    value,
    setAllowUsersBookApp,
    setWazeData,
    setSelectedServices,
    setSelectedBarber,
    setStep,
    User,
  } = useAppContext();

  useEffect(() => { userRef.current = User; }, [User]);

  // ─── Responsive ──────────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = windowWidth <= 768;
  const isDesktop = windowWidth > 1024;

  // ─── Language bootstrap ───────────────────────────────────────────────────
  useEffect(() => {
    try {
      const hasVisited = localStorage.getItem("hasVisited");
      const savedLang = localStorage.getItem("lang");
      if (!hasVisited) {
        setShowLanguageModal(true);
      } else if (savedLang) {
        i18n.changeLanguage(JSON.parse(savedLang));
      }
    } catch {
      localStorage.removeItem("lang");
      localStorage.removeItem("hasVisited");
      setShowLanguageModal(true);
    }
  }, [i18n]);

  const handleLanguageSelect = (code) => {
    try {
      i18n.changeLanguage(code);
      localStorage.setItem("lang", JSON.stringify(code));
      localStorage.setItem("hasVisited", "true");
      setShowLanguageModal(false);
    } catch (e) {
      console.error(e);
    }
  };

  // ─── Data ────────────────────────────────────────────────────────────────
  const GetAllApps = useCallback((resetBooking = false) => {
    axios
      .post(`${MyDomain}Barbers2/GetAllBarberData`, {
        PhoneNumber: BarberPhoneNumber,
      })
      .then(({ data }) => {
        setAllowUsersBookApp(data.barberData.allowUsersBookApp);
        setAllApps(data.barberAppointments);
        setAllData(data);
        setWazeData(data.barberShopPlace);

        const list = [];
        if (data.barberData) list.push(data.barberData);
        if (data.allBarbers?.length) list.push(...data.allBarbers);
        const hasMultipleBarbers = list.length > 1;
        setOtherBarbers(hasMultipleBarbers ? list : []);

        const today = moment().format("DD-MM-YYYY");
        setChoosedApps(data.barberAppointments.filter((a) => a.date === today));
        setChoosedDate(moment().format("DD-MM-YYYY"));

        if (resetBooking) {
          setSelectedBarber(null);
          setSelectedServices([]);
          setStep(userRef.current ? (hasMultipleBarbers ? 4 : 5) : 1);
        }

        setLoading(true);
      })
      .catch(console.error);
  }, [BarberPhoneNumber]);

  useEffect(() => {
    GetAllApps(true);
  }, [BarberPhoneNumber]);

  // ─── SignalR ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!BarberPhoneNumber) return;
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${MyDomain}appointmentHub`)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) =>
          ctx.elapsedMilliseconds < 60000 ? Math.random() * 10000 : null,
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    hubRef.current = conn;
    conn.onreconnected(() => GetAllApps());

    const start = async () => {
      try {
        if (conn.state === signalR.HubConnectionState.Disconnected) {
          await conn.start();
          conn.on("AppointmentCreated", () => {
            GetAllApps();
            setOpenDrawer(false);
          });
          conn.on("ReceiveBookingUpdate", () => {
            GetAllApps();
            setOpenDrawer(false);
          });
        }
      } catch {
        setTimeout(() => {
          if (conn.state === signalR.HubConnectionState.Disconnected) start();
        }, 5000);
      }
    };
    start();

    return () => {
      conn.off("AppointmentCreated");
      conn.off("ReceiveBookingUpdate");
      const s = conn.state;
      if (
        s === signalR.HubConnectionState.Connected ||
        s === signalR.HubConnectionState.Connecting
      )
        conn.stop().catch(() => {});
    };
  }, [BarberPhoneNumber, GetAllApps]);

  // ─── User ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const v = localStorage.getItem("user");
      if (v) setUser(JSON.parse(v));
    } catch {
    } finally {
      setAppContextLoading(false);
    }
  }, []);

  const navItems = [
    {
      label: t("TheMainPage") || "Home",
      icon: <HomeOutlinedIcon sx={{ fontSize: 22 }} />,
    },
    {
      label: t("myappointments") || "Appointments",
      icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 22 }} />,
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: C.bg,
        fontFamily: "'Segoe UI', sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @keyframes bmp-spin    { to { transform: rotate(360deg); } }
        @keyframes bmp-fadeUp  { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes bmp-shimmer { 0%,100%{opacity:.6} 50%{opacity:1} }
        .bmp-lang-btn:hover  { border-color: ${C.accent} !important; background: ${C.accentLight} !important; }
        .bmp-nav-tab:hover   { background: rgba(201,168,76,0.10) !important; }
      `}</style>

      {/* ══════════════ LANGUAGE MODAL ══════════════ */}
      {showLanguageModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.75)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            style={{
              background: C.primary,
              borderRadius: 24,
              border: `1px solid rgba(255,255,255,0.1)`,
              maxWidth: 420,
              width: "100%",
              padding: isMobile ? "32px 24px" : "40px 36px",
              boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
              animation: "bmp-fadeUp 0.4s ease both",
            }}
          >
            {/* Icon */}
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${C.accent} 0%, #E8B84B 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
                boxShadow: `0 8px 24px ${C.accent}55`,
              }}
            >
              <span style={{ fontSize: 32 }}>✂️</span>
            </div>

            {/* Title */}
            <h2
              style={{
                margin: "0 0 8px",
                textAlign: "center",
                fontSize: isMobile ? 22 : 26,
                fontWeight: 800,
                color: "#fff",
              }}
            >
              {t("WelcomeToNeholTorem") || "Welcome to Bokahit! 👋"}
            </h2>
            <p
              style={{
                margin: "0 0 28px",
                textAlign: "center",
                fontSize: 14,
                color: C.textMuted,
              }}
            >
              {t("SelectYourLanguage") ||
                "Please select your preferred language"}
            </p>

            {/* Language options */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  className="bmp-lang-btn"
                  onClick={() => handleLanguageSelect(lang.code)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "14px 18px",
                    borderRadius: 14,
                    border: `1.5px solid rgba(255,255,255,0.12)`,
                    background: "rgba(255,255,255,0.05)",
                    cursor: "pointer",
                    transition: "all 0.18s",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 14 }}
                  >
                    <span style={{ fontSize: 28 }}>{lang.flag}</span>
                    <span
                      style={{ fontSize: 16, fontWeight: 600, color: "#fff" }}
                    >
                      {lang.name}
                    </span>
                  </div>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={C.accent}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              ))}
            </div>

            <p
              style={{
                margin: "22px 0 0",
                textAlign: "center",
                fontSize: 12,
                color: "rgba(255,255,255,0.3)",
              }}
            >
              {t("CanChangeLater") || "You can change this later from the menu"}
            </p>
          </div>
        </div>
      )}

      {/* ══════════════ DESKTOP TOP NAV TABS ══════════════ */}
      {!OpenDrawer && isDesktop && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 24,
            zIndex: 1100,
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(16px)",
            borderRadius: 16,
            border: `1px solid ${C.border}`,
            boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
            padding: 5,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            animation: "bmp-fadeUp 0.4s ease both",
          }}
        >
          {navItems.map((item, i) => (
            <button
              key={i}
              className="bmp-nav-tab"
              onClick={(e) => {
                e.stopPropagation();
                setValue(i);
              }}
              title={item.label}
              style={{
                width: 48,
                height: 48,
                borderRadius: 11,
                border: "none",
                background: value === i ? C.primary : "transparent",
                color: value === i ? C.accent : C.textSub,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                boxShadow:
                  value === i ? `0 4px 12px rgba(26,26,46,0.3)` : "none",
              }}
            >
              {React.cloneElement(item.icon, { sx: { fontSize: 21 } })}
            </button>
          ))}
        </div>
      )}

      {/* ══════════════ CONTENT ══════════════ */}
      <div
        style={{ flex: 1, paddingBottom: !isDesktop && !OpenDrawer ? 68 : 0 }}
      >
        {Loading ? (
          <>
            <div style={{ display: value === 0 ? "block" : "none" }}>
              <MPHome />
            </div>
            <div style={{ display: value === 1 ? "block" : "none" }}>
              <MPHomeApps />
            </div>
          </>
        ) : (
          /* Loading screen */
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              gap: 20,
            }}
          >
            {/* Scissors icon ring */}
            <div
              style={{
                width: 88,
                height: 88,
                borderRadius: "50%",
                background: C.accentLight,
                border: `2px solid ${C.accent}44`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: "bmp-shimmer 2s ease-in-out infinite",
              }}
            >
              <span style={{ fontSize: 38 }}>✂️</span>
            </div>

            {/* Spinner */}
            <div
              style={{
                width: 44,
                height: 44,
                border: `3px solid ${C.border}`,
                borderTop: `3px solid ${C.accent}`,
                borderRadius: "50%",
                animation: "bmp-spin 0.85s linear infinite",
              }}
            />

            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.text }}>
                {t("LoadingSalons") || "Loading…"}
              </div>
              <div style={{ fontSize: 13, color: C.textMuted, marginTop: 5 }}>
                {t("LoadingSalonsSubtitle") || "Please wait a moment"}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════ MOBILE BOTTOM TAB BAR ══════════════ */}
      {!OpenDrawer && !isDesktop && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            background: C.surface,
            borderTop: `1px solid ${C.border}`,
            boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
            display: "flex",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          {navItems.map((item, i) => {
            const active = value === i;
            return (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setValue(i);
                }}
                style={{
                  flex: 1,
                  height: 60,
                  border: "none",
                  background: "transparent",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  cursor: "pointer",
                  transition: "background 0.15s",
                  borderTop: `2.5px solid ${active ? C.accent : "transparent"}`,
                }}
              >
                <div
                  style={{
                    color: active ? C.accent : C.textMuted,
                    transition: "color 0.2s",
                    transform: active ? "scale(1.15)" : "scale(1)",
                    transition: "all 0.2s",
                  }}
                >
                  {React.cloneElement(item.icon, {
                    sx: { fontSize: active ? 23 : 21 },
                  })}
                </div>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: active ? 700 : 500,
                    color: active ? C.accent : C.textMuted,
                    letterSpacing: 0.2,
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
